/**
 * EL AVISO DE «SIN CONEXIÓN»: una banda fina arriba, solo cuando hace falta.
 *
 *   · Sin red (lo dice el sistema): «Sin conexión · viendo lo guardado (10:32)».
 *   · Con red pero una pantalla está enseñando la copia porque la red falló o no
 *     contestó a tiempo: «Conexión lenta · viendo lo guardado (10:32)».
 *
 * La hora es la de la copia más vieja que se está enseñando: lo que de verdad
 * importa saber («¿esto es de ahora o de esta mañana?»). En cuanto una lectura
 * vuelve a llegar del servidor, la banda se va sola.
 */
import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { CloudOff, WifiLow } from "lucide-react";
import { leerEstadoOffline, suscribirEstadoOffline } from "@/lib/offline/estadoOffline";

function cuando(ms: number | null, idioma: string): string | null {
  if (!ms) return null;
  const d = new Date(ms);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  try {
    return mismoDia
      ? d.toLocaleTimeString(idioma, { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleString(idioma, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

export function AvisoSinConexion() {
  const { t, i18n } = useTranslation();
  const estado = useSyncExternalStore(suscribirEstadoOffline, leerEstadoOffline, leerEstadoOffline);
  if (!estado.activo || !estado.uid) return null;
  const sinRed = !estado.enLinea;
  if (!sinRed && !estado.mostrandoGuardado) return null;

  const hora = cuando(estado.guardadoDesde, i18n.language);
  const Icono = sinRed ? CloudOff : WifiLow;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="aviso-sin-conexion"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 4px)" }}
    >
      <div className="flex max-w-[92vw] items-center gap-1.5 rounded-full bg-foreground/85 px-3 py-1 text-[12px] font-medium text-background shadow-sm backdrop-blur-sm">
        <Icono className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">
          {sinRed ? t("offline.sinConexion") : t("offline.conexionLenta")}
          {" · "}
          {hora ? t("offline.viendoGuardadoDe", { hora }) : t("offline.viendoGuardado")}
        </span>
      </div>
    </div>
  );
}
